import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  Input,
  Text,
  Tooltip,
  useTheme,
} from '@chakra-ui/react';
import {
  TextInput,
  useToastHook,
  PlusIcon,
  EditPencilIcon,
  TrashIcon,
  InfoCircle,
} from '@devd-client/devd/components';
import { Field, Formik, FieldArray, FormikProps } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import { ChakraStylesConfig, Select } from 'chakra-react-select';
import {
  generateId,
  GROUP_LEVEL_METRICS,
  operatorOptions,
} from '../../helpers/Goals.utils';
import { validationSchema } from './validationSchema';
import { useGetMetricsList, useUpdateGoalKeyResults } from '../../apis';
import {
  CreateGoalKeyResultsValues,
  CreateGoalKeyResultsFormValues,
  GoalKeyResult,
  Metric,
} from '../../helpers/goals.types';

const getInitialValues = (keyResultState: GoalKeyResult[] | undefined) => {
  const defaultKeyResult = {
    id: generateId('kr'),
    title: '',
    metricName: '',
    metricOperator: '',
    targetValue: '',
    weight: '',
  };

  if (Array.isArray(keyResultState) && keyResultState?.length > 0) {
    return {
      keyResults: keyResultState.map((kr) => ({
        id: kr?.id || generateId('kr'),
        title: kr?.title || '',
        metricName: kr?.metricName || '',
        metricOperator: kr?.metricOperator || '',
        targetValue: kr?.targetValue?.toString() || '',
        weight: kr?.weight?.toString() || '',
      })),
    };
  }

  return {
    keyResults: [defaultKeyResult],
  };
};

interface CreateGoalKeyResultsProps {
  goalId?: string;
  goalScope?: string;
  isEdit?: boolean;
  keyResultState?: GoalKeyResult[];
}

const CreateGoalKeyResults = ({
  goalId,
  goalScope,
  isEdit,
  keyResultState,
}: CreateGoalKeyResultsProps) => {
  const { colors } = useTheme();
  const [newToast] = useToastHook();
  const queryClient = useQueryClient();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const { data: metricsList, isLoading: metricsListLoading } =
    useGetMetricsList();
  const { mutate: updateKeyResults, isLoading: updateKeyResultsLoading } =
    useUpdateGoalKeyResults();

  const formRef = useRef<FormikProps<CreateGoalKeyResultsFormValues>>(null);

  const formKey = useMemo(
    () =>
      `${goalId || 'new'}-${
        keyResultState ? JSON.stringify(keyResultState) : 'empty'
      }`,
    [goalId, keyResultState]
  );

  const initialValues = useMemo(
    () => getInitialValues(keyResultState),
    [keyResultState]
  );

  useEffect(() => {
    if (formRef.current) {
      formRef.current.resetForm({ values: initialValues });
    }
  }, [initialValues]);

  const onSubmit = (values: CreateGoalKeyResultsFormValues) => {
    const payload: Array<{
      title: string;
      metricName: string;
      metricOperator: string;
      targetValue: number;
      weight: number;
    }> =
      values?.keyResults?.map((kr: CreateGoalKeyResultsValues) => ({
        title: kr?.title,
        metricName: kr?.metricName,
        metricOperator: kr?.metricOperator,
        targetValue: Number(kr?.targetValue) || 0,
        weight: Number(kr?.weight) || 0,
      })) || [];

    if (!goalId) {
      newToast({
        message: 'Goal ID is required to update key results',
        status: 'error',
      });
      return;
    }

    updateKeyResults(
      {
        goalId,
        keyResults: payload,
      },
      {
        onSuccess: () => {
          newToast({
            message: `Key results ${
              isEdit ? 'updated' : 'created'
            } successfully!`,
            status: 'success',
          });
          queryClient.invalidateQueries(['goalsData']);
        },
        onError: (error) => {
          newToast({
            message: `Failed to ${
              isEdit ? 'update' : 'create'
            } key results. Please try again.`,
            status: 'error',
          });
        },
      }
    );
  };

  return (
    <Box w={'800px'}>
      <Formik
        key={formKey}
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {(props) => {
          const { handleSubmit, setFieldValue, errors, touched, values } =
            props;
          const totalWeight = values?.keyResults?.reduce(
            (sum: number, kr: CreateGoalKeyResultsValues) =>
              sum + (Number(kr?.weight) || 0),
            0
          );
          return (
            <form onSubmit={handleSubmit}>
              <FieldArray name="keyResults">
                {({ push, remove }) => {
                  return (
                    <>
                      <Flex direction={'column'} gap={4}>
                        {values.keyResults?.map(
                          (
                            _: CreateGoalKeyResultsValues | undefined,
                            index: number
                          ) => {
                            const hasErrors =
                              errors.keyResults &&
                              typeof errors.keyResults === 'object' &&
                              Array.isArray(errors.keyResults) &&
                              errors.keyResults[index] &&
                              Object.keys(errors.keyResults[index]).length >
                                0 &&
                              touched.keyResults &&
                              typeof touched.keyResults === 'object' &&
                              Array.isArray(touched.keyResults) &&
                              touched.keyResults[index];

                            const isExpanded = expandedIndex === index;

                            return (
                              <Box
                                key={values.keyResults[index].id}
                                border={'1px solid'}
                                borderColor={
                                  hasErrors && !isExpanded
                                    ? 'error'
                                    : 'text.tertiary'
                                }
                                rounded={'8px'}
                                p={isExpanded ? 4 : 2}
                                px={4}
                              >
                                {!isExpanded && (
                                  <Flex
                                    justify="space-between"
                                    align="center"
                                    borderBottom={
                                      isExpanded ? '1px solid' : 'none'
                                    }
                                    borderColor={'text.tertiary'}
                                  >
                                    <Text
                                      fontSize="sm"
                                      fontWeight="medium"
                                      color={'text.gray.300'}
                                    >
                                      {index + 1}.{' '}
                                      {values.keyResults[index].title ||
                                        'Untitled Key Result'}
                                    </Text>
                                    <Flex gap={1} align={'center'}>
                                      <Tooltip
                                        label="Edit key result"
                                        placement="bottom"
                                        hasArrow
                                      >
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() =>
                                            setExpandedIndex(index)
                                          }
                                        >
                                          <EditPencilIcon />
                                        </Button>
                                      </Tooltip>
                                      <Box
                                        w={'1px'}
                                        h={'16px'}
                                        bg={'text.secondary2'}
                                      />
                                      <Tooltip
                                        label="Delete key result"
                                        placement="bottom"
                                        hasArrow
                                      >
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => remove(index)}
                                        >
                                          <TrashIcon />
                                        </Button>
                                      </Tooltip>
                                    </Flex>
                                  </Flex>
                                )}
                                {isExpanded && (
                                  <>
                                    <Grid
                                      templateColumns="repeat(12, 1fr)"
                                      gap={6}
                                    >
                                      <GridItem colSpan={8}>
                                        <TextInput
                                          name={`keyResults.${index}.title`}
                                          label={`${index + 1}. Key Result`}
                                          placeholder="Key result"
                                          isRequired
                                        />
                                      </GridItem>
                                      <GridItem colSpan={4}>
                                        <Field
                                          name={`keyResults.${index}.weight`}
                                        >
                                          {({ field, form }: any) => (
                                            <FormControl
                                              id="weight"
                                              isInvalid={
                                                form.errors.keyResults?.[index]
                                                  ?.weight &&
                                                form.touched.keyResults?.[index]
                                                  ?.weight
                                              }
                                            >
                                              <Flex
                                                align="center"
                                                gap={1}
                                                mb={1}
                                              >
                                                <Text
                                                  fontSize="sm"
                                                  fontFamily="heading"
                                                  fontWeight="semibold"
                                                  color="text.secondary2"
                                                >
                                                  Weight *
                                                </Text>
                                                <Tooltip
                                                  label="Assign a weight (%) to this KR, reflecting its importance to the Objective. All KR weights under an Objective must sum to 100%."
                                                  placement="top"
                                                  hasArrow
                                                >
                                                  <Box>
                                                    <InfoCircle
                                                      height={'14px'}
                                                      width={'14px'}
                                                    />
                                                  </Box>
                                                </Tooltip>
                                              </Flex>
                                              <Box
                                                position="relative"
                                                width="100%"
                                              >
                                                <Input
                                                  {...field}
                                                  placeholder="Weight"
                                                  borderColor={
                                                    colors.text.tertiary
                                                  }
                                                  type="number"
                                                  min="0"
                                                  max="100"
                                                  paddingRight="30px"
                                                />
                                                <Text
                                                  position="absolute"
                                                  right="10px"
                                                  top="50%"
                                                  transform="translateY(-50%)"
                                                  pointerEvents="none"
                                                >
                                                  %
                                                </Text>
                                              </Box>
                                              <FormErrorMessage position="absolute">
                                                {typeof form.errors
                                                  .keyResults?.[index]
                                                  ?.weight === 'string'
                                                  ? form.errors.keyResults[
                                                      index
                                                    ].weight
                                                  : 'Weight is required'}
                                              </FormErrorMessage>
                                            </FormControl>
                                          )}
                                        </Field>
                                      </GridItem>

                                      <GridItem colSpan={12}>
                                        <Text
                                          fontSize="sm"
                                          fontFamily="heading"
                                          fontWeight="semibold"
                                          color="text.secondary2"
                                          mb={1}
                                        >
                                          Select Metric *
                                        </Text>
                                        <Flex align="flex-end" gap={4}>
                                          <Box>
                                            <Field
                                              name={`keyResults.${index}.metricName`}
                                            >
                                              {({ field, form }: any) => (
                                                <FormControl
                                                  id="metricName"
                                                  isInvalid={
                                                    form.errors.keyResults?.[
                                                      index
                                                    ]?.metricName &&
                                                    form.touched.keyResults?.[
                                                      index
                                                    ]?.metricName
                                                  }
                                                >
                                                  <Select
                                                    name={`keyResults.${index}.metricName`}
                                                    placeholder="Select metric"
                                                    options={metricsList?.dto?.map(
                                                      (metric: Metric) => ({
                                                        label: metric?.title,
                                                        value: metric?.name,
                                                      })
                                                    )}
                                                    value={
                                                      metricsList?.dto
                                                        ?.map(
                                                          (metric: Metric) => ({
                                                            label:
                                                              metric?.title,
                                                            value: metric?.name,
                                                          })
                                                        )
                                                        ?.find(
                                                          (option: {
                                                            label: string;
                                                            value: string;
                                                          }) => {
                                                            const storedValue =
                                                              values
                                                                ?.keyResults[
                                                                index
                                                              ].metricName;
                                                            if (
                                                              option.value ===
                                                              'CUSTOM'
                                                            ) {
                                                              return (
                                                                option.label ===
                                                                storedValue
                                                              );
                                                            }
                                                            return (
                                                              option.value ===
                                                              storedValue
                                                            );
                                                          }
                                                        ) || null
                                                    }
                                                    onChange={(option: {
                                                      label: string;
                                                      value: string;
                                                    }) => {
                                                      const selectedMetricValue =
                                                        option.value ===
                                                        'CUSTOM'
                                                          ? option.label || ''
                                                          : option.value || '';

                                                      if (
                                                        goalScope ===
                                                          'CONTRIBUTOR' &&
                                                        GROUP_LEVEL_METRICS.includes(
                                                          selectedMetricValue
                                                        )
                                                      ) {
                                                        const selectedMetric =
                                                          metricsList?.dto?.find(
                                                            (metric: Metric) =>
                                                              metric?.name ===
                                                                selectedMetricValue ||
                                                              (selectedMetricValue ===
                                                                metric?.title &&
                                                                metric?.name ===
                                                                  'CUSTOM')
                                                          );

                                                        const metricTitle =
                                                          selectedMetric?.title ||
                                                          option.label ||
                                                          selectedMetricValue;

                                                        newToast({
                                                          message: `${metricTitle} is a group level metric and cannot be measured at an individual level.`,
                                                          status: 'error',
                                                          duration: 3000,
                                                        });

                                                        setFieldValue(
                                                          `keyResults.${index}.metricName`,
                                                          ''
                                                        );
                                                        return;
                                                      }

                                                      setFieldValue(
                                                        `keyResults.${index}.metricName`,
                                                        selectedMetricValue
                                                      );
                                                    }}
                                                    chakraStyles={{
                                                      container: (
                                                        provided: ChakraStylesConfig
                                                      ) => ({
                                                        ...provided,
                                                        width: '200px',
                                                      }),
                                                      control: (
                                                        provided: ChakraStylesConfig
                                                      ) => ({
                                                        ...provided,
                                                        borderColor:
                                                          colors.text.tertiary,
                                                        boxShadow: 'none',
                                                        borderRadius: '4px',
                                                        minHeight: '40px',
                                                        '&:hover': {
                                                          borderColor:
                                                            'gray.300',
                                                        },
                                                      }),
                                                      dropdownIndicator: (
                                                        provided: ChakraStylesConfig
                                                      ) => ({
                                                        ...provided,
                                                        backgroundColor:
                                                          'transparent',
                                                        padding: '8px',
                                                      }),
                                                    }}
                                                    components={{
                                                      IndicatorSeparator: () =>
                                                        null,
                                                    }}
                                                    isDisabled={
                                                      metricsListLoading
                                                    }
                                                  />
                                                  <FormErrorMessage position="absolute">
                                                    {typeof form.errors
                                                      .keyResults?.[index]
                                                      ?.metricName === 'string'
                                                      ? form.errors.keyResults[
                                                          index
                                                        ].metricName
                                                      : 'Metric is required'}
                                                  </FormErrorMessage>
                                                </FormControl>
                                              )}
                                            </Field>
                                          </Box>

                                          <Box>
                                            <Field
                                              name={`keyResults.${index}.metricOperator`}
                                            >
                                              {({ field, form }: any) => (
                                                <FormControl
                                                  id="metricOperator"
                                                  isInvalid={
                                                    form.errors.keyResults?.[
                                                      index
                                                    ]?.metricOperator &&
                                                    form.touched.keyResults?.[
                                                      index
                                                    ]?.metricOperator
                                                  }
                                                >
                                                  <Select
                                                    name={`keyResults.${index}.metricOperator`}
                                                    placeholder="More than"
                                                    options={operatorOptions}
                                                    value={
                                                      operatorOptions?.find(
                                                        (option: {
                                                          value: string;
                                                        }) =>
                                                          option?.value ===
                                                          values?.keyResults[
                                                            index
                                                          ].metricOperator
                                                      ) || null
                                                    }
                                                    onChange={(option: {
                                                      value: string;
                                                    }) => {
                                                      setFieldValue(
                                                        `keyResults.${index}.metricOperator`,
                                                        option?.value || ''
                                                      );
                                                    }}
                                                    chakraStyles={{
                                                      container: (
                                                        provided: ChakraStylesConfig
                                                      ) => ({
                                                        ...provided,
                                                        width: '200px',
                                                      }),
                                                      control: (
                                                        provided: ChakraStylesConfig
                                                      ) => ({
                                                        ...provided,
                                                        borderColor:
                                                          colors.text.tertiary,
                                                        boxShadow: 'none',
                                                        borderRadius: '4px',
                                                        minHeight: '40px',
                                                        '&:hover': {
                                                          borderColor:
                                                            'gray.300',
                                                        },
                                                      }),
                                                      dropdownIndicator: (
                                                        provided: ChakraStylesConfig
                                                      ) => ({
                                                        ...provided,
                                                        backgroundColor:
                                                          'transparent',
                                                        padding: '8px',
                                                      }),
                                                    }}
                                                    components={{
                                                      IndicatorSeparator: () =>
                                                        null,
                                                    }}
                                                  />
                                                  <FormErrorMessage position="absolute">
                                                    {typeof form.errors
                                                      .keyResults?.[index]
                                                      ?.metricOperator ===
                                                    'string'
                                                      ? form.errors.keyResults[
                                                          index
                                                        ].metricOperator
                                                      : 'Comparison operator is required'}
                                                  </FormErrorMessage>
                                                </FormControl>
                                              )}
                                            </Field>
                                          </Box>

                                          <Box flex="1">
                                            <Text
                                              mt={'-27px'}
                                              fontSize={'sm'}
                                              color={'text.secondary2'}
                                              fontWeight={'semibold'}
                                              mb={1}
                                            >
                                              {values.keyResults[index]
                                                ?.metricName
                                                ? (() => {
                                                    const metric =
                                                      metricsList?.dto?.find(
                                                        (metric: Metric) =>
                                                          metric?.name ===
                                                            values?.keyResults[
                                                              index
                                                            ]?.metricName ||
                                                          (values?.keyResults[
                                                            index
                                                          ]?.metricName ===
                                                            metric?.title &&
                                                            metric?.name ===
                                                              'CUSTOM')
                                                      );
                                                    const yLabel =
                                                      metric?.y_label;
                                                    const avgLabel =
                                                      metric?.average_label;

                                                    if (yLabel && avgLabel) {
                                                      return (
                                                        <Text>
                                                          {avgLabel}{' '}
                                                          <Text
                                                            as={'span'}
                                                            fontSize={'xs'}
                                                            fontWeight={
                                                              'medium'
                                                            }
                                                          >
                                                            ({yLabel})
                                                          </Text>
                                                        </Text>
                                                      );
                                                    }
                                                    return (
                                                      yLabel ||
                                                      avgLabel ||
                                                      'Target Value'
                                                    );
                                                  })()
                                                : 'Target Value'}
                                            </Text>
                                            <Field
                                              name={`keyResults.${index}.targetValue`}
                                            >
                                              {({ field, form }: any) => (
                                                <FormControl
                                                  id="targetValue"
                                                  isInvalid={
                                                    form.errors.keyResults?.[
                                                      index
                                                    ]?.targetValue &&
                                                    form.touched.keyResults?.[
                                                      index
                                                    ]?.targetValue
                                                  }
                                                >
                                                  {values.keyResults[index]
                                                    .metricOperator ===
                                                  'BETWEEN' ? (
                                                    <Flex align="center">
                                                      <Input
                                                        value={
                                                          (field.value || '')
                                                            .toString()
                                                            .split('-')[0] || ''
                                                        }
                                                        onChange={(e) => {
                                                          const secondValue =
                                                            (field.value || '')
                                                              .toString()
                                                              .split('-')[1] ||
                                                            '';
                                                          setFieldValue(
                                                            `keyResults.${index}.targetValue`,
                                                            `${e.target.value}-${secondValue}`
                                                          );
                                                        }}
                                                        placeholder="Min value"
                                                        borderColor={
                                                          colors.text.tertiary
                                                        }
                                                        width="45%"
                                                        type="number"
                                                        required
                                                      />
                                                      <Text
                                                        mx={2}
                                                        fontWeight="medium"
                                                      >
                                                        -
                                                      </Text>
                                                      <Input
                                                        value={
                                                          (field.value || '')
                                                            .toString()
                                                            .split('-')[1] || ''
                                                        }
                                                        onChange={(e) => {
                                                          const firstValue =
                                                            (field.value || '')
                                                              .toString()
                                                              .split('-')[0] ||
                                                            '';
                                                          setFieldValue(
                                                            `keyResults.${index}.targetValue`,
                                                            `${firstValue}-${e.target.value}`
                                                          );
                                                        }}
                                                        placeholder="Max value"
                                                        borderColor={
                                                          colors.text.tertiary
                                                        }
                                                        width="45%"
                                                        type="number"
                                                        required
                                                      />
                                                    </Flex>
                                                  ) : (
                                                    <Input
                                                      {...field}
                                                      placeholder="Target value"
                                                      borderColor={
                                                        colors.text.tertiary
                                                      }
                                                      type="number"
                                                      required
                                                    />
                                                  )}
                                                  <FormErrorMessage position="absolute">
                                                    {typeof form.errors
                                                      .keyResults?.[index]
                                                      ?.targetValue === 'string'
                                                      ? form.errors.keyResults[
                                                          index
                                                        ].targetValue
                                                      : 'Target value is required'}
                                                  </FormErrorMessage>
                                                </FormControl>
                                              )}
                                            </Field>
                                          </Box>
                                        </Flex>
                                      </GridItem>
                                    </Grid>
                                    <Flex w={'full'} justify={'flex-end'}>
                                      <Tooltip
                                        label="Delete key result"
                                        placement="right"
                                        hasArrow
                                      >
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => remove(index)}
                                          mt={'12px'}
                                          mb={'-8px'}
                                        >
                                          <TrashIcon />
                                        </Button>
                                      </Tooltip>
                                    </Flex>
                                  </>
                                )}
                              </Box>
                            );
                          }
                        )}

                        <Button
                          mt={4}
                          backgroundColor="#F9F8F8"
                          leftIcon={<PlusIcon color="#2A2A2F" />}
                          border="1px solid #E0E0E0"
                          fontSize="sm"
                          fontWeight="semibold"
                          color="text.gray.300"
                          borderRadius="4px"
                          w={'fit-content'}
                          onClick={() => {
                            const newIndex = values.keyResults?.length;
                            push({
                              id: generateId('kr'),
                              title: '',
                              metricName: '',
                              metricOperator: '',
                              targetValue: '',
                              weight: '',
                            });
                            setExpandedIndex(newIndex);
                          }}
                        >
                          Add New Key Result
                        </Button>
                      </Flex>
                      <Divider mt={'24px'} mb={'12px'} />
                      {totalWeight !== 100 && (
                        <Text color="error" fontSize="sm" mb={'12px'}>
                          Total weight must equal 100%. (Current total:{' '}
                          {totalWeight}%)
                        </Text>
                      )}
                      <Button
                        type="submit"
                        variant="filled"
                        isDisabled={
                          updateKeyResultsLoading ||
                          totalWeight !== 100 ||
                          values.keyResults?.length === 0
                        }
                        isLoading={updateKeyResultsLoading}
                        w={'fit-content'}
                        mt={totalWeight !== 100 ? '0' : '12px'}
                      >
                        {isEdit ? 'Update Key Results' : 'Add Key Results'}
                      </Button>
                    </>
                  );
                }}
              </FieldArray>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CreateGoalKeyResults;
